import React from 'react';
import { Box, Grid, Card, CardContent, Typography, Button, IconButton, Chip, List, ListItem, ListItemText, Divider } from '@mui/material';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningIcon from '@mui/icons-material/Warning';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title as ChartTitle, Tooltip, Legend } from 'chart.js';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';
import { ANALYTICS_DATA } from '../data/mockData';

// Register ChartJS modules
ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTitle, Tooltip, Legend);

export default function DashboardView() {
    const { tables, orders, onlineOrders, acceptOnlineOrder, rejectOnlineOrder, inventory, accountingLedger, subscribedModules, hotelName, currentUser } = useApp();
    const { t, currentLanguage } = useLanguage();

    let activeBranchId = currentUser?.branch_id;
    if (!activeBranchId) {
        try {
            const u = sessionStorage.getItem('react_pos_current_user');
            if (u) activeBranchId = JSON.parse(u)?.branch_id;
        } catch(e) {}
    }
    if (!activeBranchId) {
        activeBranchId = localStorage.getItem('dinexa_active_branch_id');
    }

    const branchOrders = activeBranchId ? orders.filter(o => !o.branch_id || String(o.branch_id) === String(activeBranchId)) : orders;

    // 1. Calculations
    const occupiedTablesCount = tables.filter(t => t.status === 'occupied' || t.status === 'billed').length;
    const activeKotsCount = branchOrders.filter(o => o.status === 'preparing').length;

    const completedOrders = branchOrders.filter(o => o.status === 'served');
    
    // Revenue splits
    let liveCashRevenue = 0;
    let liveUpiRevenue = 0;
    let liveAggregatorRevenue = 0;

    completedOrders.forEach(o => {
        const method = (o.paymentMethod || o.payment_method || '').toLowerCase();
        if (method === 'cash') {
            liveCashRevenue += o.total;
        } else if (method === 'upi' || method === 'online') {
            liveUpiRevenue += o.total;
        } else if (method === 'zomato' || method === 'swiggy') {
            liveAggregatorRevenue += o.total;
        } else if (method.includes('split')) {
            const cashMatch = method.match(/cash:\s*₹?(\d+(\.\d+)?)/i);
            const upiMatch = method.match(/upi:\s*₹?(\d+(\.\d+)?)/i);
            if (cashMatch) liveCashRevenue += parseFloat(cashMatch[1]);
            if (upiMatch) liveUpiRevenue += parseFloat(upiMatch[1]);
        }
    });

    const liveRevenue = liveCashRevenue + liveUpiRevenue + liveAggregatorRevenue;
    const liveOrdersCount = completedOrders.length;

    const totalRevenue = ANALYTICS_DATA.todaySales + liveRevenue;
    const totalOrdersCompleted = ANALYTICS_DATA.todayOrders + liveOrdersCount;

    // Total expenses today
    const totalExpensesToday = accountingLedger.reduce((sum, e) => sum + e.amount, 0);

    // Low stock alerts
    const lowStockItems = inventory.filter(item => item.stock <= item.minStock);

    // 2. Setup chart data
    const chartData = {
        labels: ANALYTICS_DATA.hourlyLabels,
        datasets: [
            {
                label: t('dash-hourly-sales'),
                data: ANALYTICS_DATA.hourlyValues.map((val, idx) => {
                    if (idx === ANALYTICS_DATA.hourlyValues.length - 1) {
                        return val + liveRevenue;
                    }
                    return val;
                }),
                backgroundColor: 'rgba(99, 102, 241, 0.85)', // Indigo
                borderColor: '#008751',
                borderWidth: 1,
                borderRadius: 6
            }
        ]
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: {
                    color: 'rgba(0, 0, 0, 0.05)'
                }
            },
            x: {
                grid: {
                    display: false
                }
            }
        }
    };

    const getDisplayName = (item) => {
        if (!item) return '';
        if (currentLanguage === 'mr') return item.nameMr || item.name;
        if (currentLanguage === 'hi') return item.nameHi || item.name;
        return item.name;
    };

    const stats = [
        { title: t('dash-revenue'), value: `₹${totalRevenue.toLocaleString('en-IN')}`, icon: <CurrencyRupeeIcon sx={{ color: '#008751' }} />, bg: 'rgba(0, 135, 81, 0.1)' },
        { title: t('dash-orders'), value: totalOrdersCompleted, icon: <ShoppingBagIcon sx={{ color: '#0d9488' }} />, bg: 'rgba(13, 148, 136, 0.1)' },
        { title: t('dash-occupied'), value: `${occupiedTablesCount} / ${tables.length}`, icon: <TableRestaurantIcon sx={{ color: '#d97706' }} />, bg: 'rgba(217, 119, 6, 0.1)' },
        { title: t('dash-active-kots'), value: activeKotsCount, icon: <LocalFireDepartmentIcon sx={{ color: '#ef4444' }} />, bg: 'rgba(239, 68, 68, 0.1)' }
    ];

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f8fafc', minHeight: 'calc(100vh - 70px)' }}>
            
            {/* ============================================================ */}
            {/* 1. MOBILE-ONLY OPTIMIZED DASHBOARD (For Remote Owner)       */}
            {/* ============================================================ */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 3 }}>
                
                {/* Mobile Header Greeting */}
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 900, color: '#0f172a' }}>
                        Remote Owner Control Board
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                        {hotelName || 'My Restaurant'} • Live Status
                    </Typography>
                </Box>

                {/* Key Metrics Quick List */}
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <CardContent sx={{ p: 2, textAlign: 'center' }}>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>LIVE REVENUE</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 900, color: '#008751', mt: 0.5 }}>
                                    ₹{totalRevenue}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={6}>
                        <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <CardContent sx={{ p: 2, textAlign: 'center' }}>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>COMPLETED ORDERS</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 900, color: '#0d9488', mt: 0.5 }}>
                                    {totalOrdersCompleted}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={6}>
                        <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <CardContent sx={{ p: 2, textAlign: 'center' }}>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>TABLE OCCUPANCY</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 900, color: '#d97706', mt: 0.5 }}>
                                    {occupiedTablesCount} / {tables.length}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={6}>
                        <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <CardContent sx={{ p: 2, textAlign: 'center' }}>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>ACTIVE KOTS</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 900, color: '#ef4444', mt: 0.5 }}>
                                    {activeKotsCount}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Cash Drawer & Expenses Summary Card */}
                <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <CardContent sx={{ p: 2.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccountBalanceWalletIcon sx={{ color: '#008751', fontSize: 20 }} />
                            Cash & Expense Book Today
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, borderBottom: '1px dashed #cbd5e1', pb: 1 }}>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>Total Outward Expenses:</Typography>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: '#ef4444' }}>-₹{totalExpensesToday}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="caption" sx={{ color: '#475569', fontWeight: 700 }}>Live Net Cash Sales:</Typography>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: '#008751' }}>+₹{liveCashRevenue}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="caption" sx={{ color: '#475569', fontWeight: 700 }}>Direct UPI / Online:</Typography>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: '#0ea5e9' }}>+₹{liveUpiRevenue}</Typography>
                            </Box>
                            {liveAggregatorRevenue > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, pt: 1, borderTop: '1px dashed #cbd5e1' }}>
                                    <Typography variant="caption" sx={{ color: '#d97706', fontWeight: 700 }}>Aggregators (Zomato/Swiggy):</Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#d97706' }}>₹{liveAggregatorRevenue} (Pending)</Typography>
                                </Box>
                            )}
                        </Box>
                    </CardContent>
                </Card>

                {/* Critical Inventory Stock Alerts */}
                <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <CardContent sx={{ p: 2.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <WarningIcon sx={{ color: '#f59e0b', fontSize: 20 }} />
                            Critical Inventory Alerts ({lowStockItems.length})
                        </Typography>
                        {lowStockItems.length === 0 ? (
                            <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 700 }}>
                                ✓ All raw materials are at safe levels.
                            </Typography>
                        ) : (
                            <List size="small" disablePadding>
                                {lowStockItems.slice(0, 3).map(item => (
                                    <ListItem key={item.id} sx={{ px: 0, py: 0.5, display: 'flex', justifyContent: 'space-between' }}>
                                        <ListItemText 
                                            primary={getDisplayName(item)} 
                                            primaryTypographyProps={{ fontSize: '0.75rem', fontWeight: 700 }}
                                        />
                                        <Chip 
                                            label={`${item.stock} ${item.unit} left`} 
                                            size="small" 
                                            color="error" 
                                            sx={{ fontWeight: 800, fontSize: '0.65rem', height: 18 }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </CardContent>
                </Card>

                {/* Pending Online Orders Quick Aggregator (Zomato & Swiggy Feature Flag) */}
                {subscribedModules?.includes('zomato_swiggy') && (
                    <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 2.5 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5, color: '#0f172a' }}>
                                Pending Online Channels ({onlineOrders.length})
                            </Typography>
                            {onlineOrders.length === 0 ? (
                                <Typography variant="caption" sx={{ color: '#64748b' }}>No pending orders.</Typography>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {onlineOrders.slice(0, 2).map(order => (
                                        <Box key={order.id} sx={{ p: 1.5, border: '1px solid #fed7aa', borderRadius: 2, bgcolor: '#fff7ed' }}>
                                            <Box sx={{ display: 'flex', justify: 'space-between', mb: 0.5 }}>
                                                <Chip label={order.source.toUpperCase()} size="small" sx={{ bgcolor: '#ea580c', color: '#fff', fontSize: '0.55rem', height: 16 }} />
                                                <Typography variant="caption" sx={{ fontWeight: 800 }}>₹{order.total}</Typography>
                                            </Box>
                                            <Typography variant="caption" sx={{ fontSize: '0.65rem', display: 'block', color: '#64748b' }}>
                                                {order.items.map(i => i.name).join(', ')}
                                            </Typography>
                                            <Button 
                                                variant="contained" 
                                                color="success" 
                                                size="small" 
                                                onClick={() => acceptOnlineOrder(order.id)}
                                                sx={{ mt: 1, py: 0.25, fontSize: '0.65rem' }}
                                            >
                                                Accept
                                            </Button>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                )}
            </Box>

            {/* ============================================================ */}
            {/* 2. DESKTOP/TABLET FULL WEB DASHBOARD                        */}
            {/* ============================================================ */}
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                {/* Top row cards */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    {stats.map((stat, index) => (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                            <Card sx={{ 
                                border: '1px solid #e2e8f0',
                                boxShadow: 'none',
                                borderRadius: 3
                            }}>
                                <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3 }}>
                                    <Box>
                                        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 700, mb: 0.5 }}>
                                            {stat.title}
                                        </Typography>
                                        <Typography variant="h4" sx={{ fontWeight: 900, color: '#0f172a' }}>
                                            {stat.value}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ p: 1.5, borderRadius: 2.5, bgcolor: stat.bg, display: 'flex' }}>
                                        {stat.icon}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Charts & Online Integration */}
                <Grid container spacing={3}>
                    {/* Sales Chart */}
                    <Grid item xs={12} md={subscribedModules?.includes('zomato_swiggy') ? 8 : 12}>
                        <Card sx={{ 
                            border: '1px solid #e2e8f0',
                            boxShadow: 'none',
                            borderRadius: 3,
                            height: 380
                        }}>
                            <CardContent sx={{ p: 3, height: '100%' }}>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', mb: 2 }}>
                                    {t('dash-hourly-sales')}
                                </Typography>
                                <Box sx={{ height: 280 }}>
                                    <Bar data={chartData} options={chartOptions} />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Online Orders Aggregator (Zomato & Swiggy Feature Flag) */}
                    {subscribedModules?.includes('zomato_swiggy') && (
                        <Grid item xs={12} md={4}>
                            <Card sx={{ 
                                border: '1px solid #e2e8f0',
                                boxShadow: 'none',
                                borderRadius: 3,
                                height: 380,
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                            <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', mb: 2 }}>
                                    {t('dash-online-hub')}
                                </Typography>
                                
                                <Box sx={{ 
                                    flexGrow: 1, 
                                    overflowY: 'auto',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2
                                }}>
                                    {onlineOrders.length === 0 ? (
                                        <Box sx={{ 
                                            display: 'flex', 
                                            flexDirection: 'column',
                                            alignItems: 'center', 
                                            justifyContent: 'center',
                                            py: 6,
                                            color: '#94a3b8'
                                        }}>
                                            <CheckCircleIcon sx={{ color: '#10b981', fontSize: 36, mb: 1 }} />
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {t('dash-no-online')}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        onlineOrders.map(order => {
                                            const isSwiggy = order.source.toLowerCase() === 'swiggy';
                                            return (
                                                <Card key={order.id} variant="outlined" sx={{ 
                                                    p: 2, 
                                                    borderRadius: 2.5,
                                                    borderColor: isSwiggy ? '#fed7aa' : '#fecaca',
                                                    bgcolor: isSwiggy ? '#fff7ed' : '#fef2f2',
                                                    boxShadow: 'none'
                                                }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                        <Chip
                                                            label={order.source.toUpperCase()}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: isSwiggy ? '#ea580c' : '#dc2626',
                                                                color: '#ffffff',
                                                                fontWeight: 800,
                                                                fontSize: '0.65rem',
                                                                height: 20
                                                            }}
                                                        />
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#0f172a' }}>
                                                            ₹{order.total}
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="caption" display="block" sx={{ fontWeight: 700, color: '#475569' }}>
                                                        ID: {order.id} | Cust: {order.customerName}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#64748b', fontStyle: 'italic', display: 'block', mb: 1.5 }}>
                                                        {order.items.map(i => `${i.name} x ${i.quantity}`).join(', ')}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            color="success"
                                                            fullWidth
                                                            onClick={() => acceptOnlineOrder(order.id)}
                                                            sx={{ textTransform: 'none', fontWeight: 700 }}
                                                        >
                                                            {t('accept')}
                                                        </Button>
                                                        <IconButton
                                                            color="error"
                                                            size="small"
                                                            onClick={() => rejectOnlineOrder(order.id)}
                                                            sx={{ border: '1px solid #fecaca', borderRadius: 1.5 }}
                                                        >
                                                            <CancelIcon />
                                                        </IconButton>
                                                    </Box>
                                                </Card>
                                            );
                                        })
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    )}
                </Grid>
            </Box>
        </Box>
    );
}
